import { useState, useEffect } from 'react';
import { api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Globe, Save, Loader2, RefreshCw, Plus, X, Link2 } from 'lucide-react';

/**
 * Site Settings Page (Super Admin Only)
 * Manages global site settings for the main domain
 */
const SiteSettings = () => {
  const { t } = useTranslation();
  
  const [settings, setSettings] = useState({
    site_name: '',
    default_title: '',
    default_description: '',
    main_domain: '',
    domain_aliases: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshingCache, setRefreshingCache] = useState(false);
  const [cachedDomains, setCachedDomains] = useState([]);
  const [newAlias, setNewAlias] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/config/site-settings');
      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error) {
      toast.error('Ayarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/config/site-settings', settings);
      toast.success('Site ayarları kaydedildi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ayarlar kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshCache = async () => {
    setRefreshingCache(true);
    try {
      const response = await api.post('/config/domain/refresh');
      if (response.data.success) {
        setCachedDomains(response.data.data.cached_domains || []);
        toast.success(`Domain cache yenilendi (${response.data.data.cached_domains?.length || 0} domain)`);
      }
    } catch (error) {
      toast.error('Cache yenilenemedi');
    } finally {
      setRefreshingCache(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleAddAlias = () => {
    const trimmed = newAlias.trim().toLowerCase();
    if (!trimmed) {
      toast.error('Lütfen bir domain girin');
      return;
    }
    
    // Basic domain validation
    if (!/^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,}$/.test(trimmed)) {
      toast.error('Geçersiz domain formatı (örn: example.com)');
      return;
    }
    
    if (settings.domain_aliases.includes(trimmed)) {
      toast.error('Bu domain zaten ekli');
      return;
    }
    
    if (trimmed === settings.main_domain) {
      toast.error('Ana domain ile aynı olamaz');
      return;
    }
    
    setSettings(prev => ({
      ...prev,
      domain_aliases: [...prev.domain_aliases, trimmed]
    }));
    setNewAlias('');
    toast.success(`${trimmed} eklendi`);
  };

  const handleRemoveAlias = (domain) => {
    setSettings(prev => ({
      ...prev,
      domain_aliases: prev.domain_aliases.filter(d => d !== domain)
    }));
    toast.info(`${domain} kaldırıldı`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Site Ayarları</h1>
          <p className="text-slate-600 dark:text-slate-300 mt-1">
            Ana domain (hrportal.be) için global site ayarları
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleRefreshCache}
          disabled={refreshingCache}
        >
          {refreshingCache ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Domain Cache Yenile
        </Button>
      </div>

      {/* Main Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-600" />
            Global Site Ayarları
          </CardTitle>
          <CardDescription>
            Bu ayarlar sadece ana domain için geçerlidir. Firma domain'leri kendi ayarlarını kullanır.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Site Name */}
          <div className="space-y-2">
            <Label htmlFor="site_name">Site Adı</Label>
            <Input
              id="site_name"
              value={settings.site_name}
              onChange={(e) => handleChange('site_name', e.target.value)}
              placeholder="OMS Portal"
            />
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Sistemin genel adı (menü ve footer'da görünür)
            </p>
          </div>

          {/* Default Title */}
          <div className="space-y-2">
            <Label htmlFor="default_title">Varsayılan Başlık (SEO)</Label>
            <Input
              id="default_title"
              value={settings.default_title}
              onChange={(e) => handleChange('default_title', e.target.value)}
              placeholder="OMS Portal - İnsan Kaynakları Yönetimi"
            />
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Tarayıcı sekmesinde görünen başlık
            </p>
          </div>

          {/* Default Description */}
          <div className="space-y-2">
            <Label htmlFor="default_description">Varsayılan Açıklama (SEO)</Label>
            <Textarea
              id="default_description"
              value={settings.default_description}
              onChange={(e) => handleChange('default_description', e.target.value)}
              placeholder="Çok firmalı İK yönetim sistemi"
              rows={3}
            />
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Arama motorlarında görünen açıklama (max 300 karakter)
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {t('common.save')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Domain Management Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-purple-600" />
            Domain Yönetimi
          </CardTitle>
          <CardDescription>
            Ana domain ve alias domain'leri yönetin. Tüm domain'ler aynı sisteme yönlendirilir.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Main Domain */}
          <div className="space-y-2">
            <Label htmlFor="main_domain">Ana Domain</Label>
            <Input
              id="main_domain"
              value={settings.main_domain}
              onChange={(e) => handleChange('main_domain', e.target.value.toLowerCase())}
              placeholder="hrportal.be"
            />
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Sistemin birincil domain adresi
            </p>
          </div>

          {/* Domain Aliases */}
          <div className="space-y-3">
            <Label>Domain Alias'ları</Label>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
              Ana domain'e ek olarak sisteme erişilebilecek alternatif domain'ler
            </p>
            
            {/* Add new alias */}
            <div className="flex gap-2">
              <Input
                value={newAlias}
                onChange={(e) => setNewAlias(e.target.value.toLowerCase())}
                placeholder="crmportal.be"
                onKeyDown={(e) => e.key === 'Enter' && handleAddAlias()}
              />
              <Button onClick={handleAddAlias} variant="outline">
                <Plus className="h-4 w-4 mr-1" />
                Ekle
              </Button>
            </div>

            {/* Alias list */}
            {settings.domain_aliases?.length > 0 ? (
              <div className="flex flex-wrap gap-2 mt-3">
                {settings.domain_aliases.map((alias) => (
                  <Badge 
                    key={alias} 
                    variant="secondary"
                    className="px-3 py-1.5 text-sm font-mono flex items-center gap-2"
                  >
                    {alias}
                    <button
                      onClick={() => handleRemoveAlias(alias)}
                      className="hover:text-red-600 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 italic mt-2">
                Henüz domain alias eklenmemiş
              </p>
            )}
          </div>

          {/* Save Domains Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Domain'leri Kaydet
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Cached Domains Info */}
      {cachedDomains.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Önbellekteki Domain'ler</CardTitle>
            <CardDescription>
              CORS ve domain çözümlemesi için cache'lenen domain listesi
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {cachedDomains.map((domain) => (
                <span 
                  key={domain}
                  className="px-3 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-full text-sm font-mono"
                >
                  {domain}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Globe className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-blue-900 mb-1">Çoklu Domain Mimarisi</h4>
              <p className="text-sm text-blue-700">
                Her firma kendi domain'ine sahip olabilir (örn: hr.firma.com). 
                Domain ayarları Firmalar sayfasından yönetilir. 
                Yeni domain eklendiğinde CORS otomatik güncellenir, restart gerekmez.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SiteSettings;
